import React, { useState, useRef, useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

// Nur EIN Plus-Knopf je Seite. Der Firmen-Verlauf bringt seinen eigenen mit, samt
// Firma, Kontakten und Vorgang — der globale (GlobalActivitySpeedDial, ohne Kontext)
// tritt zurueck, solange einer davon steht.
let mountedDials = 0;
const dialListeners = new Set();
const notifyDials = () => dialListeners.forEach((listener) => listener());

export const useSpeedDialTaken = () =>
  useSyncExternalStore(
    (onChange) => {
      dialListeners.add(onChange);
      return () => dialListeners.delete(onChange);
    },
    () => mountedDials > 0
  );

/**
 * Floating Speed Dial action button.
 * Expands to offer quick creation of Meetings, Tasks, Emails, and Notes.
 */
const ActivitySpeedDial = (props) => {
  const {
    onNewCall,
    onNewMeeting,
    onNewTask,
    onNewEmail,
    onNewNote,
    global: isGlobal,
  } = props;
  const { t } = useTranslation(["components", "common", "tasks"]);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Der globale meldet sich NICHT an — er wuerde sich sonst selbst verdraengen.
  useEffect(() => {
    if (isGlobal) return undefined;
    mountedDials += 1;
    notifyDials();
    return () => {
      mountedDials -= 1;
      notifyDials();
    };
  }, [isGlobal]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("touchstart", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpen]);

  const actions = [
    {
      key: "call",
      label: t("Start a call"),
      icon: "mdi mdi-phone-outline",
      color: "#16a34a",
      bg: "#dcfce7",
      onClick: () => {
        setIsOpen(false);
        if (onNewCall) onNewCall();
      },
    },
    {
      key: "note",
      label: t("New note"),
      icon: "mdi mdi-note-text-outline",
      color: "#d97706",
      bg: "#fef3c7",
      onClick: () => {
        setIsOpen(false);
        if (onNewNote) onNewNote();
      },
    },
    {
      key: "email",
      label: t("New e-mail"),
      icon: "mdi mdi-email-outline",
      color: "#0284c7",
      bg: "#e0f2fe",
      onClick: () => {
        setIsOpen(false);
        if (onNewEmail) onNewEmail();
      },
    },
    {
      key: "task",
      label: t("New task"),
      icon: "mdi mdi-checkbox-marked-outline",
      color: "#dc222f",
      bg: "#fee2e2",
      onClick: () => {
        setIsOpen(false);
        if (onNewTask) onNewTask();
      },
    },
    {
      key: "meeting",
      label: t("Log a meeting"),
      icon: "mdi mdi-calendar-check-outline",
      color: "#d97706",
      bg: "#fef3c7",
      onClick: () => {
        setIsOpen(false);
        if (onNewMeeting) onNewMeeting();
      },
    },
  ];

  return (
    <div
      ref={containerRef}
      className="activity-speed-dial"
      style={{
        position: "fixed",
        right: "0.9rem",
        zIndex: 1040,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "0.6rem",
      }}
    >
      {/* Speed dial expanded action items */}
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "0.55rem",
            marginBottom: "0.2rem",
            animation: "fadeInUp 0.18s ease-out",
          }}
        >
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              onClick={action.onClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.55rem",
                padding: "0.4rem 0.85rem 0.4rem 0.6rem",
                borderRadius: "2rem",
                backgroundColor: "#ffffff",
                border: `1px solid ${action.color}33`,
                boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
                cursor: "pointer",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.04)";
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.12)";
              }}
            >
              <span
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: action.bg,
                  color: action.color,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                  flexShrink: 0,
                }}
              >
                <i className={action.icon} />
              </span>
              <span
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  whiteSpace: "nowrap",
                }}
              >
                {action.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Main floating "+" button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          width: "3rem",
          height: "3rem",
          borderRadius: "50%",
          backgroundColor: isOpen ? "#475569" : "#dc222f",
          color: "#ffffff",
          border: "none",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.5rem",
          cursor: "pointer",
          transition: "transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease",
          transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
          outline: "none",
        }}
        title={isOpen ? t("Close") : t("Create activity")}
      >
        <i className={isOpen ? "mdi mdi-close" : "mdi mdi-plus"} />
      </button>
    </div>
  );
};

export default ActivitySpeedDial;
